import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Skeleton from '../components/Skeleton';
import { notifyError, notifySuccess, notifyWarning } from '../utils/statusShortcuts';
import { normalizePlanHighlightsForBotLimit } from '../utils/planHighlights';
import './SubscriptionPage.css';

const STATUS_LABELS = {
  active: 'Aktiv',
  trial: 'Prøveperiode',
  past_due: 'Forfalt',
  canceled: 'Avsluttet',
  paused: 'Stoppet',
  pending: 'Avventer',
};

const formatProviderLabel = (value) => {
  const labels = { stripe: 'Stripe (kort)', invoice: 'Faktura', internal: 'KIChat' };
  return labels[value?.toLowerCase()] || value || 'KIChat';
};

const formatStripeStatusLabel = (status) => {
  const map = {
    active: 'Aktiv', trialing: 'Prøveperiode', past_due: 'Forfalt',
    incomplete: 'Venter på betaling', incomplete_expired: 'Utløpt',
    canceled: 'Avsluttet', unpaid: 'Ubetalt', paused: 'Stoppet',
  };
  return map[status?.toLowerCase()] || status || 'Ukjent';
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('nb-NO', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const shortId = (value) => {
  if (!value) return '—';
  return value.length <= 12 ? value : `${value.slice(0, 6)}…${value.slice(-4)}`;
};

const extractPlanHighlights = (plan, limit = 5) => {
  if (!plan) return [];
  const candidates = [
    plan.features?.marketingHighlights,
    plan.metadata?.marketingFeatures,
    plan.metadata?.marketing_features,
    plan.features?.highlights,
  ];
  for (const candidate of candidates) {
    const list = Array.isArray(candidate)
      ? candidate
      : (typeof candidate === 'string'
        ? candidate.split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
        : []);
    if (list.length > 0) {
      const normalized = normalizePlanHighlightsForBotLimit(plan, list);
      return limit ? normalized.slice(0, limit) : normalized;
    }
  }
  return [];
};

const NOK = new Intl.NumberFormat('nb-NO', {
  style: 'currency',
  currency: 'NOK',
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? NOK.format(num) : '—';
};

const formatNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('nb-NO') : '0';
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const parseDateKey = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      return new Date(year, month, day);
    }
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatChartDate = (value) => {
  const date = parseDateKey(value);
  if (!date) return '—';
  return date.toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toDateKey = (value) => {
  const date = parseDateKey(value);
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const USAGE_CHART_CYCLE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const startOfLocalDay = (value) => {
  const date = parseDateKey(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const diffLocalDays = (start, end) => {
  const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.floor((endUtc - startUtc) / DAY_MS);
};

const normalizePeriodEndExclusive = (value) => {
  const parsed = parseDateKey(value);
  if (!parsed) return null;
  const dayStart = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  return parsed.getTime() > dayStart.getTime()
    ? addDays(dayStart, 1)
    : dayStart;
};

export const buildUsageChartPeriod = ({
  periodStart,
  periodEnd,
  today = new Date(),
  cycleDays = USAGE_CHART_CYCLE_DAYS,
} = {}) => {
  const normalizedCycleDays = Math.max(1, Math.floor(Number(cycleDays) || USAGE_CHART_CYCLE_DAYS));
  const start = startOfLocalDay(periodStart);
  const endExclusive = normalizePeriodEndExclusive(periodEnd);

  if (start && endExclusive && start < endExclusive) {
    const totalDays = Math.max(1, diffLocalDays(start, endExclusive));
    const cycleCount = Math.max(1, Math.ceil(totalDays / normalizedCycleDays));
    const todayStart = startOfLocalDay(today) || start;
    let cycleIndex = 0;

    if (todayStart >= endExclusive) {
      cycleIndex = cycleCount - 1;
    } else if (todayStart >= start) {
      cycleIndex = Math.min(
        Math.floor(diffLocalDays(start, todayStart) / normalizedCycleDays),
        cycleCount - 1,
      );
    }

    const cycleStart = addDays(start, cycleIndex * normalizedCycleDays);
    const cycleEnd = addDays(cycleStart, normalizedCycleDays);
    return { start: cycleStart, end: cycleEnd };
  }

  const now = startOfLocalDay(today) || new Date();
  const fallbackStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return { start: fallbackStart, end: addDays(fallbackStart, normalizedCycleDays) };
};

export const buildUsageChartSeries = ({
  dailyUsage,
  chartPeriod,
} = {}) => {
  const rawDailyUsage = Array.isArray(dailyUsage) ? dailyUsage : [];
  const usageMap = new Map();
  rawDailyUsage.forEach((entry) => {
    const key = toDateKey(entry?.date || entry?.day || entry?.usageDate);
    if (!key) return;
    usageMap.set(key, {
      tokens: Number(entry?.tokens ?? 0),
      costNok: Number(entry?.costNok ?? entry?.usageCostNok ?? 0),
    });
  });

  const series = [];
  if (!chartPeriod?.start || !chartPeriod?.end || !(chartPeriod.start < chartPeriod.end)) {
    return series;
  }

  const cursor = new Date(chartPeriod.start);
  while (cursor < chartPeriod.end) {
    const key = toDateKey(cursor);
    const daily = usageMap.get(key) || { tokens: 0, costNok: 0 };
    series.push({
      date: key,
      tokens: daily.tokens,
      costNok: daily.costNok,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return series;
};

const getPlanBasePrice = (plan) => {
  if (!plan) return null;
  const role = normalizePlanRole(
    plan?.features?.planRole
    || plan?.metadata?.planRole
    || plan?.metadata?.plan_role
    || plan?.features?.plan_role,
  );
  const code = String(plan.code || plan.metadata?.plan_code || plan.metadata?.planCode || '').trim().toLowerCase();
  if (role === 'payasyougo' || code === 'basic') return 0;
  const stripe = plan.stripePrices ? Object.values(plan.stripePrices)[0] : null;
  if (stripe && Number.isFinite(Number(stripe.unitAmount))) {
    return Number(stripe.unitAmount);
  }
  return Number.isFinite(Number(plan.monthlyPrice)) ? Number(plan.monthlyPrice) : null;
};

const normalizePlanRole = (value) => {
  if (!value) return null;
  const normalized = value.toString().trim().toLowerCase();
  if (!normalized) return null;
  if (['gratis', 'free', 'free_tier', 'freetier'].includes(normalized)) return 'free';
  if (['premium', 'pro'].includes(normalized)) return 'premium';
  if (['payasyougo', 'pay_as_you_go', 'payg', 'usage', 'metered'].includes(normalized)) return 'payasyougo';
  if (['paid', 'betalt'].includes(normalized)) return 'paid';
  if (['enterprise', 'bedrift', 'business'].includes(normalized)) return 'enterprise';
  return normalized;
};

const normalizeBillingMode = (value) => {
  if (!value) return null;
  const normalized = value.toString().trim().toLowerCase();
  if (['metered', 'usage', 'payg', 'pay_as_you_go', 'payasyougo', 'pay-as-you-go'].includes(normalized)) return 'metered';
  if (['subscription', 'recurring', 'flat'].includes(normalized)) return 'subscription';
  return null;
};

const getPlanRole = (plan) => normalizePlanRole(
  plan?.features?.planRole
  || plan?.metadata?.planRole
  || plan?.metadata?.plan_role
  || plan?.features?.plan_role,
);

const getBillingMode = (plan) => {
  const explicit = normalizeBillingMode(
    plan?.features?.billingMode
    || plan?.metadata?.billingMode
    || plan?.metadata?.billing_mode
    || plan?.features?.billing_mode,
  );
  if (explicit) return explicit;
  if (plan?.features?.usagePriceLookupKey || plan?.features?.usage_price_lookup_key) {
    return 'metered';
  }
  const price = Number(getPlanBasePrice(plan) ?? 0);
  return Number.isFinite(price) && price > 0 ? 'subscription' : 'metered';
};

const getPlanUsageLabel = (plan, fallbackTokenPrice) => {
  if (!plan) return null;
  const billingMode = getBillingMode(plan);
  if (billingMode === 'subscription') return 'Ubegrenset tokens';
  const includedCredit = Number(
    plan.features?.signupCreditNok ?? plan.features?.includedCreditNok ?? 0,
  );
  if (includedCredit > 0) {
    return `${formatCurrency(includedCredit)} i startkreditt`;
  }
  const fairUse = Number(plan.features?.fairUseTokens ?? 0);
  if (fairUse > 0) {
    return `${formatNumber(fairUse)} tokens (fair use)`;
  }
  const unitPrice = Number(plan.features?.tokenPricePer1k ?? fallbackTokenPrice ?? 0);
  if (unitPrice > 0) {
    return `${formatCurrency(unitPrice)} per 1k tokens`;
  }
  return null;
};

const getPlanMonthlyCredit = (plan) => {
  if (!plan) return null;
  const sources = [plan.features || {}, plan.metadata || {}];
  for (const source of sources) {
    for (const key of ['monthlyCreditNok', 'monthlyCredit', 'monthly_credit_nok', 'monthly_credit']) {
      const raw = source?.[key];
      const value = Number(raw);
      if (Number.isFinite(value)) {
        return Math.max(0, value);
      }
    }
  }
  return null;
};

const buildPlanFeatureList = (plan, fallbackTokenPrice) => {
  if (!plan) return [];
  return dedupeHighlights([
    getPlanUsageLabel(plan, fallbackTokenPrice),
    ...extractPlanHighlights(plan),
  ]);
};

const dedupeHighlights = (items) => {
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    const trimmed = String(item || '').trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(trimmed);
  });
  return result;
};

const isFreePlan = (plan) => {
  if (!plan) return false;
  const role = getPlanRole(plan);
  if (role === 'free' || role === 'payasyougo') return true;
  const planCode = String(plan.code || plan.metadata?.plan_code || plan.metadata?.planCode || '').trim().toLowerCase();
  if (planCode === 'basic') return true;
  const price = Number(getPlanBasePrice(plan) ?? 0);
  return getBillingMode(plan) === 'metered' && (!Number.isFinite(price) || price <= 0);
};

const isContactOnlyPlan = (plan) => {
  if (!plan) return false;
  const role = getPlanRole(plan);
  if (role === 'enterprise') return true;
  const flag =
    plan?.metadata?.contactOnly
    ?? plan?.metadata?.contact_only
    ?? plan?.features?.contactOnly
    ?? plan?.features?.contact_only;
  if (flag === true || flag === 'true') {
    return true;
  }
  const displayPrice = plan?.metadata?.displayPrice;
  if (displayPrice && typeof displayPrice === 'string') {
    return displayPrice.toLowerCase().includes('kontakt');
  }
  return false;
};

const REQUIRED_FIELDS = [
  { key: 'companyName', label: 'Selskapsnavn' },
  { key: 'organizationNumber', label: 'Organisasjonsnummer' },
  { key: 'billingContactEmail', label: 'Faktura-epost' },
];

const SubscriptionPage = () => {
  const { selectedCompany, selectedCompanyId, refreshCompanies } = useAuth();
  const companyId = selectedCompanyId || selectedCompany?.id || null;

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const setError = () => {};
  const [success, setSuccess] = useState(null);
  const [planLoadingCode, setPlanLoadingCode] = useState(null);

  // Checkout flow state
  const [checkoutStep, setCheckoutStep] = useState(null); // null | 'details' | 'processing'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({
    companyName: '',
    organizationNumber: '',
    billingContactEmail: '',
    billingContactName: '',
    billingAddressLine1: '',
    billingPostalCode: '',
    billingCity: '',
  });
  const [detailsError, setDetailsError] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);

  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  const loadSummary = useCallback(async () => {
    if (!companyId) {
      setSummary(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }
    const cachedFull = DashboardApi.peekBillingSummary(companyId, { skipStripe: false, maxAgeMs: 5 * 60_000 });
    if (cachedFull) {
      setSummary(cachedFull);
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
      setRefreshing(true);
    }
    setError(null);
    try {
      // Phase 1: fast (DB-backed) summary.
      const fast = await DashboardApi.getBillingSummary(companyId, {
        skipStripe: true,
        maxAgeMs: 15_000,
        retries: 0,
      });
      setSummary(fast);
      setLoading(false);

      // Phase 2: full Stripe-hydrated summary.
      const data = await DashboardApi.getBillingSummary(companyId, {
        skipStripe: false,
        force: true,
        retries: 2,
      });
      setSummary(data);
      // Pre-fill company details from profile
      if (data?.company) {
        const profile = await DashboardApi.getCustomerProfile(companyId);
        setCompanyDetails((prev) => ({
          ...prev,
          companyName: profile.companyName || prev.companyName,
          organizationNumber: profile.organizationNumber || prev.organizationNumber,
          billingContactEmail: profile.billingContactEmail || prev.billingContactEmail,
          billingContactName: profile.billingContactName || prev.billingContactName,
          billingAddressLine1: profile.billingAddressLine1 || prev.billingAddressLine1,
          billingPostalCode: profile.billingPostalCode || prev.billingPostalCode,
          billingCity: profile.billingCity || prev.billingCity,
        }));
      }
    } catch (err) {
      const message = err.message || 'Kunne ikke hente abonnementsdata.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente abonnement', source: 'Abonnement' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (success) {
      notifySuccess(success, { source: 'Abonnement' });
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  const subscription = summary?.subscription || null;
  const billingAccess = summary?.billingAccess || {};
  const requiresPaymentMethod = Boolean(billingAccess.requiresPaymentMethod);
  const isBillingBlocked = Boolean(billingAccess.blocked);
  const plans = useMemo(() => {
    if (!summary?.availablePlans) return [];
    return [...summary.availablePlans].sort((a, b) => {
      const orderA = Number(a.metadata?.uiOrder ?? 999);
      const orderB = Number(b.metadata?.uiOrder ?? 999);
      if (orderA !== orderB) return orderA - orderB;
      const priceA = getPlanBasePrice(a);
      const priceB = getPlanBasePrice(b);
      if (priceA !== priceB) {
        if (priceA === null) return 1;
        if (priceB === null) return -1;
        return priceA - priceB;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [summary?.availablePlans]);

  const currentPlanCode = subscription?.planCode || null;
  const currentPlan = plans.find((p) => p.code === currentPlanCode) || null;
  const billingMode = getBillingMode(currentPlan);
  const isSubscriptionPlan = billingMode === 'subscription';
  const premiumPlan = plans.find((p) => getPlanRole(p) === 'premium')
    || plans.find((p) => getBillingMode(p) === 'subscription')
    || plans.find((p) => getPlanBasePrice(p) > 0);
  const freePlan = plans.find((p) => isFreePlan(p));
  const paidPlanActive = Boolean(currentPlan && !isFreePlan(currentPlan));

  // Billing provider data
  const paymentProvider = subscription?.paymentProvider || 'internal';
  const providerSnapshot = subscription?.providerSnapshot || null;
  const providerStatus = subscription?.providerStatus || providerSnapshot?.status || null;
  const providerSyncedAt = subscription?.providerSyncedAt || providerSnapshot?.fetchedAt || null;
  const providerSubscriptionId = subscription?.providerSubscriptionId || null;
  const cancelAtPeriodEnd = Boolean(subscription?.cancelAtPeriodEnd);
  const hasPaymentStatus = Boolean(
    providerStatus
    || providerSyncedAt
    || providerSubscriptionId
    || (paymentProvider && paymentProvider !== 'internal'),
  );

  const usageSummary = summary?.usageThisPeriod || {};
  const dailyUsageTokensTotal = useMemo(() => {
    if (!Array.isArray(usageSummary?.dailyUsage)) return 0;
    return usageSummary.dailyUsage.reduce((sum, entry) => {
      const value = Number(entry?.tokens ?? 0);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
  }, [usageSummary?.dailyUsage]);
  const tokensUsedRaw = Number(usageSummary.tokensUsed ?? 0);
  const tokensUsedNormalized = Number.isFinite(tokensUsedRaw) ? tokensUsedRaw : 0;
  const tokensUsed = Math.max(tokensUsedNormalized, dailyUsageTokensTotal);
  const usageCostNok = Number(usageSummary.usageCostNok ?? 0);
  const creditBalanceNok = Number(usageSummary.creditBalanceNok ?? usageSummary.includedCreditNok ?? 0);
  const creditRemainingNok = Number(usageSummary.creditRemainingNok ?? 0);
  const upcomingInvoice = summary?.upcomingInvoice || null;
  const upcomingAmountDue =
    upcomingInvoice && Number.isFinite(Number(upcomingInvoice.amountDue))
      ? Number(upcomingInvoice.amountDue)
      : null;
  const upcomingInvoiceLabel = upcomingAmountDue !== null ? formatCurrency(upcomingAmountDue) : null;
  const amountDueNok = Number(usageSummary.amountDueNok ?? 0);
  const fairUseTokens = Number(usageSummary.fairUseTokens ?? 0);
  const tokenPricePer1k = Number(usageSummary.tokenPricePer1k ?? 0);
  const periodStart = usageSummary.periodStart ?? null;
  const periodEnd = usageSummary.periodEnd ?? null;
  const monthlyCreditNok = getPlanMonthlyCredit(currentPlan);
  const planPriceAmount = getPlanBasePrice(currentPlan);
  const planPriceLabel = formatCurrency(planPriceAmount ?? 0);
  const subscriptionStatusKey = (subscription?.status || 'active').toLowerCase();
  const subscriptionStatusLabel = STATUS_LABELS[subscriptionStatusKey] || subscription?.status || 'Aktiv';
  const planHeading = currentPlan?.name || 'Abonnement';
  const planStatusMessage = isSubscriptionPlan
    ? 'Ditt abonnement er aktivt og fornyes automatisk.'
    : 'Du er på en forbruksbasert plan.';
  const usageCostSummary = isSubscriptionPlan
    ? `${currentPlan?.name || 'Premium'} - ubegrenset`
    : formatCurrency(usageCostNok);
  const usageFootnote = isSubscriptionPlan
    ? 'Inkludert i abonnementet.'
    : creditBalanceNok > 0
      ? `Startkreditt igjen: ${formatCurrency(creditRemainingNok)}`
      : `Til fakturering: ${formatCurrency(amountDueNok)}`;
  const usageChartLabel = isSubscriptionPlan ? 'Tokenbruk per dag' : 'Kostnad per dag';
  const usageChartHint = isSubscriptionPlan ? 'Hold musen over for detaljer' : 'Hold musen over for tokens';

  const missingFields = REQUIRED_FIELDS.filter(
    (f) => !companyDetails[f.key] || !companyDetails[f.key].trim(),
  );
  const detailsComplete = missingFields.length === 0;

  const chartPeriod = useMemo(() => {
    return buildUsageChartPeriod({ periodStart, periodEnd });
  }, [periodStart, periodEnd]);

  const usageChartSeries = useMemo(() => {
    return buildUsageChartSeries({
      dailyUsage: usageSummary?.dailyUsage,
      chartPeriod,
    });
  }, [usageSummary?.dailyUsage, chartPeriod]);

  const usageChartMax = useMemo(
    () => usageChartSeries.reduce((max, day) => {
      const value = isSubscriptionPlan ? Number(day.tokens || 0) : Number(day.costNok || 0);
      return Math.max(max, value);
    }, 0),
    [usageChartSeries, isSubscriptionPlan],
  );

  const chartPeriodDisplayEnd = chartPeriod?.end
    ? addDays(chartPeriod.end, -1)
    : null;
  const periodRangeLabel = chartPeriod?.start && chartPeriodDisplayEnd
    ? `${formatChartDate(chartPeriod.start)} – ${formatChartDate(chartPeriodDisplayEnd)}`
    : `${formatChartDate(chartPeriod.start)} – ${formatChartDate(chartPeriod.end)}`;
  const usageCostCaption = isSubscriptionPlan ? 'Inkludert i abonnementet' : periodRangeLabel;

  const handleDetailsChange = (field, value) => {
    setCompanyDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDetailsAndCheckout = async () => {
    if (!detailsComplete) {
      const message = 'Fyll ut alle påkrevde felter før du fortsetter.';
      setDetailsError(message);
      notifyError(message, { source: 'Abonnement' });
      return;
    }
    if (!selectedPlan || !companyId) return;

    setSavingDetails(true);
    setDetailsError(null);

    try {
      // Save company details first
      await DashboardApi.updateCustomerProfile(companyId, {
        company_name: companyDetails.companyName,
        organization_number: companyDetails.organizationNumber,
        billing_contact_email: companyDetails.billingContactEmail,
        billing_contact_name: companyDetails.billingContactName,
        billing_address_line1: companyDetails.billingAddressLine1,
        billing_postal_code: companyDetails.billingPostalCode,
        billing_city: companyDetails.billingCity,
      });

      setCheckoutStep('processing');

      // Start Stripe checkout
      const response = await DashboardApi.startPlanCheckout(companyId, {
        planCode: selectedPlan.code,
        billingCycle: 'monthly',
        paymentMethod: 'card',
        successUrl: `${window.location.origin}/abonnement?status=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/abonnement?status=cancel`,
      });

      if (response.status === 'updated') {
        setSuccess('Abonnementet ble aktivert!');
        setCheckoutStep(null);
        setSelectedPlan(null);
        await loadSummary();
        await refreshCompanies();
        return;
      }

      if (response.status === 'stripe' && response.sessionId) {
        const stripe = await stripePromise;
        if (!stripe) {
          const message = 'Kortbetaling er ikke tilgjengelig. Prøv igjen senere.';
          setDetailsError(message);
          notifyError(message, { source: 'Abonnement' });
          setCheckoutStep('details');
          return;
        }
        const { error: redirectError } = await stripe.redirectToCheckout({
          sessionId: response.sessionId,
        });
        if (redirectError) {
          const message = redirectError.message || 'Kunne ikke starte betalingen.';
          setDetailsError(message);
          notifyError(message, { title: 'Kunne ikke starte betaling', source: 'Abonnement' });
          setCheckoutStep('details');
        }
        return;
      }

      setDetailsError('Uventet svar fra serveren. Prøv igjen.');
      notifyError('Uventet svar fra serveren. Prøv igjen.', { source: 'Abonnement' });
      setCheckoutStep('details');
    } catch (err) {
      const message = err.message || 'Kunne ikke starte betalingen.';
      setDetailsError(message);
      notifyError(message, { title: 'Kunne ikke starte betaling', source: 'Abonnement' });
      setCheckoutStep('details');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleCancelCheckout = () => {
    setCheckoutStep(null);
    setSelectedPlan(null);
    setDetailsError(null);
  };

  const handlePlanChange = async (plan) => {
    if (!plan || !companyId || plan.code === currentPlanCode) return;
    if (isContactOnlyPlan(plan)) {
      window.location.href = 'mailto:hei@kichat.no?subject=Enterprise-avtale';
      return;
    }
    if (isFreePlan(plan) && paidPlanActive) {
      const message = 'Kanseller den aktive planen før du går tilbake til basicnivå.';
      setError(message);
      notifyWarning(message, { source: 'Abonnement' });
      return;
    }
    const hasStripeOptions = plan.stripePrices && Object.keys(plan.stripePrices).length > 0;
    const planPrice = getPlanBasePrice(plan);
    if ((planPrice ?? 0) > 0 && hasStripeOptions) {
      setSelectedPlan(plan);
      setCheckoutStep('details');
      return;
    }
    // Direct switch for baseline plans
    setPlanLoadingCode(plan.code);
    try {
      await DashboardApi.upgradeBillingPlan(companyId, { planCode: plan.code });
      await loadSummary();
      setSuccess('Abonnementet ble oppdatert.');
    } catch (err) {
      const message = err.message || 'Kunne ikke oppdatere planen.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke oppdatere plan', source: 'Abonnement' });
    } finally {
      setPlanLoadingCode(null);
    }
  };

  const handlePlanCancellation = async () => {
    if (!companyId || !currentPlan) return;
    const msg = cancelAtPeriodEnd
      ? 'Vil du angre kanselleringen og fortsette planen?'
      : 'Vil du kansellere planen ved slutten av perioden?';
    if (!window.confirm(msg)) return;
    setPlanLoadingCode(currentPlan.code);
    try {
      await DashboardApi.cancelPlan(companyId, cancelAtPeriodEnd ? 'resume' : 'cancel');
      await loadSummary();
      setSuccess(cancelAtPeriodEnd ? 'Kanselleringen ble fjernet.' : 'Planen vil avsluttes ved periodeslutt.');
    } catch (err) {
      const message = err.message || 'Kunne ikke oppdatere planen.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke oppdatere plan', source: 'Abonnement' });
    } finally {
      setPlanLoadingCode(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    if (!companyId) return;
    try {
      const response = await DashboardApi.openBillingPortal(companyId, {
        returnUrl: window.location.href,
      });
      if (response?.url) {
        window.location.href = response.url;
      } else {
        setError('Kunne ikke åpne betalingsportalen.');
        notifyError('Kunne ikke åpne betalingsportalen.', { source: 'Abonnement' });
      }
    } catch (err) {
      const message = err.message || 'Kunne ikke åpne betalingsportalen.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke åpne betalingsportal', source: 'Abonnement' });
    }
  };

  // Check URL for success/cancel status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status === 'success') {
      setSuccess('Betalingen var vellykket! Abonnementet ditt er nå aktivt.');
      loadSummary();
      refreshCompanies();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (status === 'cancel') {
      setError('Betalingen ble avbrutt. Du kan prøve igjen når du er klar.');
      notifyWarning('Betalingen ble avbrutt. Du kan prøve igjen når du er klar.', { source: 'Abonnement' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadSummary, refreshCompanies]);

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="subscription-loading">
          <div className="subscription-skeleton-card">
            <Skeleton variant="line" size="lg" style={{ width: '220px', marginBottom: 14 }} />
            <Skeleton variant="line" style={{ width: '320px', marginBottom: 10 }} />
            <Skeleton variant="line" style={{ width: '280px', marginBottom: 18 }} />
            <Skeleton style={{ height: 110, marginBottom: 12 }} />
            <Skeleton style={{ height: 110, marginBottom: 12 }} />
            <Skeleton style={{ height: 110 }} />
          </div>
          <p className="subscription-loading-hint">Laster abonnement…</p>
        </div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="subscription-page">
        <div className="subscription-empty">
          <h2>Ingen selskap valgt</h2>
          <p>Velg et selskap for å administrere abonnementet.</p>
        </div>
      </div>
    );
  }

  // Checkout flow overlay
  if (checkoutStep) {
    return (
      <div className="subscription-page">
        <div className="subscription-checkout-overlay">
          <div className="subscription-checkout-modal">
            {checkoutStep === 'processing' ? (
              <div className="subscription-checkout-processing">
                <div className="subscription-loading-spinner large" />
                <h2>Forbereder betaling...</h2>
                <p>Du blir snart sendt til betalingssiden.</p>
              </div>
            ) : (
              <>
                <header className="subscription-checkout-header">
                  <div>
                    <span className="subscription-checkout-badge">Oppgrader</span>
                    <h2>Aktiver {selectedPlan?.name || 'Premium'}</h2>
                    <p className="subscription-checkout-price">
                      {formatCurrency(getPlanBasePrice(selectedPlan))}
                      <span>/måned</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="subscription-checkout-close"
                    onClick={handleCancelCheckout}
                    aria-label="Lukk"
                  >
                    ×
                  </button>
                </header>

                <div className="subscription-checkout-body">
                  <div className="subscription-checkout-info">
                    <h3>📋 Faktureringsdetaljer</h3>
                    <p>Vi trenger litt informasjon for å sette opp faktureringen.</p>
                  </div>

                  <form
                    className="subscription-checkout-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveDetailsAndCheckout();
                    }}
                  >
                    <div className="subscription-form-row">
                      <label className="subscription-form-field required">
                        <span>Selskapsnavn</span>
                        <input
                          type="text"
                          value={companyDetails.companyName}
                          onChange={(e) => handleDetailsChange('companyName', e.target.value)}
                          placeholder="Ditt Selskap AS"
                        />
                      </label>
                      <label className="subscription-form-field required">
                        <span>Organisasjonsnummer</span>
                        <input
                          type="text"
                          value={companyDetails.organizationNumber}
                          onChange={(e) => handleDetailsChange('organizationNumber', e.target.value)}
                          placeholder="123 456 789"
                        />
                      </label>
                    </div>

                    <div className="subscription-form-row">
                      <label className="subscription-form-field required">
                        <span>Faktura-epost</span>
                        <input
                          type="email"
                          value={companyDetails.billingContactEmail}
                          onChange={(e) => handleDetailsChange('billingContactEmail', e.target.value)}
                          placeholder="faktura@selskap.no"
                        />
                      </label>
                      <label className="subscription-form-field">
                        <span>Kontaktperson</span>
                        <input
                          type="text"
                          value={companyDetails.billingContactName}
                          onChange={(e) => handleDetailsChange('billingContactName', e.target.value)}
                          placeholder="Ola Nordmann"
                        />
                      </label>
                    </div>

                    <div className="subscription-form-row">
                      <label className="subscription-form-field">
                        <span>Adresse</span>
                        <input
                          type="text"
                          value={companyDetails.billingAddressLine1}
                          onChange={(e) => handleDetailsChange('billingAddressLine1', e.target.value)}
                          placeholder="Gateadresse"
                        />
                      </label>
                    </div>

                    <div className="subscription-form-row">
                      <label className="subscription-form-field">
                        <span>Postnummer</span>
                        <input
                          type="text"
                          value={companyDetails.billingPostalCode}
                          onChange={(e) => handleDetailsChange('billingPostalCode', e.target.value)}
                          placeholder="0000"
                        />
                      </label>
                      <label className="subscription-form-field">
                        <span>Sted</span>
                        <input
                          type="text"
                          value={companyDetails.billingCity}
                          onChange={(e) => handleDetailsChange('billingCity', e.target.value)}
                          placeholder="Oslo"
                        />
                      </label>
                    </div>

                    {detailsError && (
                      <div className="subscription-checkout-error">
                        ⚠️ {detailsError}
                      </div>
                    )}

                    <div className="subscription-checkout-actions">
                      <button
                        type="button"
                        className="subscription-btn secondary"
                        onClick={handleCancelCheckout}
                        disabled={savingDetails}
                      >
                        Avbryt
                      </button>
                      <button
                        type="submit"
                        className="subscription-btn primary"
                        disabled={savingDetails || !detailsComplete}
                      >
                        {savingDetails ? (
                          <>
                            <span className="subscription-btn-spinner" />
                            Behandler...
                          </>
                        ) : (
                          <>
                            Fortsett til betaling
                            <span className="subscription-btn-arrow">→</span>
                          </>
                        )}
                      </button>
                    </div>

                    <p className="subscription-checkout-secure">
                      🔒 Sikker betaling via Stripe. Ingen bindingstid.
                    </p>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main page content
  return (
    <div className="subscription-page">
      {isBillingBlocked && (
        <div className="subscription-alert error">
          <span>⚠️</span>
          <p>Betalingen feilet. Chatboten er deaktivert til betalingen er oppdatert.</p>
          <button type="button" className="subscription-alert-action" onClick={handleOpenBillingPortal}>
            Oppdater kort
          </button>
        </div>
      )}
      {requiresPaymentMethod && !isBillingBlocked && (
        <div className="subscription-alert warning">
          <span>💳</span>
          <p>Ingen betalingsmetode registrert. Legg til kort før kreditt er brukt opp.</p>
          <button type="button" className="subscription-alert-action" onClick={handleOpenBillingPortal}>
            Legg til kort
          </button>
        </div>
      )}

      <header className="subscription-status-header">
        <div className="subscription-status-info">
          <div className="subscription-status-badges">
            <span className={`subscription-status-badge ${subscriptionStatusKey}`}>
              {subscriptionStatusLabel}
            </span>
            {refreshing ? <span className="subscription-refreshing">Oppdaterer…</span> : null}
          </div>
          <h1>{planHeading}</h1>
          <p>{planStatusMessage}</p>
        </div>
        <div className="subscription-status-price">
          <span className="amount">{planPriceLabel}</span>
          <span className="period">/måned</span>
        </div>
      </header>

      <div className="subscription-dashboard">
        <section className="subscription-card usage">
          <h3>Forbruk</h3>
          <div className="subscription-usage-summary">
            <div className="subscription-usage-summary-item">
              <span>Akkumulert kostnad</span>
              <strong>{usageCostSummary}</strong>
              <small>{usageCostCaption}</small>
            </div>
            <div className="subscription-usage-summary-item">
              <span>Akkumulert tokenbruk</span>
              <strong>{formatNumber(tokensUsed)}</strong>
              <small>tokens</small>
            </div>
          </div>
          <div className="subscription-usage-chart">
            <div className="subscription-usage-chart-header">
              <div>
                <p>Daglig forbruk</p>
                <span>{periodRangeLabel}</span>
              </div>
              <div className="subscription-usage-chart-meta">
                <span>{usageChartLabel}</span>
                <span>{usageChartHint}</span>
              </div>
            </div>
            {usageChartSeries.length > 0 ? (
              <>
                <div
                  className="subscription-usage-chart-bars"
                  role="img"
                  aria-label={`Forbruk per dag (${periodRangeLabel})`}
                >
                  {usageChartSeries.map((day) => {
                    const max = usageChartMax || 0;
                    const scale = 90;
                    const metricValue = isSubscriptionPlan ? Number(day.tokens || 0) : Number(day.costNok || 0);
                    const percent = max > 0 ? Math.round((metricValue / max) * scale) : 0;
                    const hasUsage = metricValue > 0;
                    const height = max > 0
                      ? (hasUsage ? Math.max(10, percent) : 6)
                      : 6;
                    const dateLabel = formatChartDate(day.date);
                    const costLabel = formatCurrency(day.costNok);
                    const tokenLabel = formatNumber(day.tokens);
                    const tooltipText = isSubscriptionPlan
                      ? `${dateLabel}\nTokens: ${tokenLabel}`
                      : `${dateLabel}\nKostnad: ${costLabel}\nTokens: ${tokenLabel}`;
                    return (
                      <div
                        key={day.date}
                        className={`subscription-usage-chart-bar${hasUsage ? ' has-usage' : ' empty'}`}
                        data-tooltip={tooltipText}
                      >
                        <span style={{ height: `${height}%` }} />
                      </div>
                    );
                  })}
                </div>
                {periodRangeLabel !== '—' && (
                  <div className="subscription-usage-chart-axis">
                    <span>{formatChartDate(chartPeriod.start)}</span>
                    <span>{formatChartDate(chartPeriodDisplayEnd || chartPeriod.end)}</span>
                  </div>
                )}
              </>
            ) : (
              <p className="subscription-usage-chart-empty">Ingen forbruk registrert i perioden.</p>
            )}
          </div>
          <div className="subscription-usage-footnote">
            <span>{usageFootnote}</span>
          </div>
        </section>

        <section className="subscription-card details">
          <h3>Abonnementsdetaljer</h3>
          <dl className="subscription-details-list">
            <div>
              <dt>Status</dt>
              <dd>
                <span className={`subscription-status-dot ${subscription?.status === 'active' ? 'active' : ''}`} />
                {STATUS_LABELS[subscription?.status] || subscription?.status || 'Aktiv'}
              </dd>
            </div>
            <div>
              <dt>Plan</dt>
              <dd>{currentPlan?.name || subscription?.planCode}</dd>
            </div>
            <div>
              <dt>Neste fornyelse</dt>
              <dd>{formatDate(subscription?.currentPeriodEnd)}</dd>
            </div>
            {upcomingInvoiceLabel && (
              <div>
                <dt>Utkast til faktura</dt>
                <dd>{upcomingInvoiceLabel}</dd>
              </div>
            )}
            {!isSubscriptionPlan && (
              <div>
                <dt>{fairUseTokens > 0 ? 'Fair use-grense' : 'Startkreditt'}</dt>
                <dd>
                  {fairUseTokens > 0
                    ? formatNumber(fairUseTokens)
                    : creditBalanceNok > 0
                      ? formatCurrency(creditBalanceNok)
                      : '—'}
                </dd>
              </div>
            )}
            {monthlyCreditNok && monthlyCreditNok > 0 && (
              <div>
                <dt>Månedlig kreditt</dt>
                <dd>{formatCurrency(monthlyCreditNok)}</dd>
              </div>
            )}
            {!isSubscriptionPlan && (
              <div>
                <dt>Tokenpris</dt>
                <dd>{tokenPricePer1k > 0 ? `${formatCurrency(tokenPricePer1k)} per 1k` : '—'}</dd>
              </div>
            )}
          </dl>
        </section>

        {hasPaymentStatus && (
          <section className="subscription-card manage">
            <h3>Betalingsstatus</h3>
            <dl className="subscription-details-list">
              <div>
                <dt>Metode</dt>
                <dd>{formatProviderLabel(paymentProvider)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{formatStripeStatusLabel(providerStatus)}</dd>
              </div>
              <div>
                <dt>Sist synkronisert</dt>
                <dd>{formatDateTime(providerSyncedAt)}</dd>
              </div>
              <div>
                <dt>Referanse</dt>
                <dd>{shortId(providerSubscriptionId)}</dd>
              </div>
            </dl>
            <div className="subscription-manage-actions">
              <button type="button" className="subscription-btn secondary" onClick={handleOpenBillingPortal}>
                Administrer betaling
              </button>
            </div>
            {cancelAtPeriodEnd && (
              <p className="subscription-cancel-note">⚠️ Abonnementet avsluttes ved periodeslutt.</p>
            )}
          </section>
        )}
      </div>

      {!paidPlanActive && (
        <header className="subscription-hero">
          <div className="subscription-hero-content">
            <span className="subscription-hero-badge">{freePlan?.name || currentPlan?.name || 'Basic'}</span>
            <h1>Lås opp hele potensialet til KI Chat</h1>
            <p>
              Oppgrader til {premiumPlan?.name || 'Premium'} for ubegrenset tilgang til alle funksjoner,
              prioritert support og mer.
            </p>
          </div>
          <div className="subscription-hero-visual">
            <div className="subscription-hero-icon">🚀</div>
          </div>
        </header>
      )}

      <section className="subscription-plans-section">
        <header className="subscription-section-header">
          <h2>Tilgjengelige planer</h2>
          <p>Velg planen som matcher behovet ditt.</p>
        </header>
        {plans.length === 0 ? (
          <p className="subscription-empty-text">Ingen planer er definert.</p>
        ) : (
          <div className="subscription-plans-grid">
            {plans.map((plan) => {
              const isCurrent = plan.code === currentPlanCode;
              const contactOnly = isContactOnlyPlan(plan);
              const freePlanLocked = isFreePlan(plan) && paidPlanActive;
              const featureList = buildPlanFeatureList(plan, tokenPricePer1k);
              const priceLabel = plan.metadata?.displayPrice || formatCurrency(getPlanBasePrice(plan));
              const isPremiumUpgrade = !paidPlanActive && premiumPlan?.code === plan.code;
              const actionLabel = contactOnly
                ? 'Kontakt oss'
                : isCurrent
                  ? 'Aktiv plan'
                  : planLoadingCode === plan.code
                    ? 'Oppdaterer…'
                    : freePlanLocked
                      ? 'Kanseller først'
                      : isPremiumUpgrade
                        ? 'Oppgrader nå'
                        : 'Bytt plan';
              return (
                <article key={plan.id} className={`subscription-plan-item${isCurrent ? ' is-current' : ''}`}>
                  <div className="subscription-plan-item-head">
                    <h3>{plan.name}</h3>
                    {isCurrent && <span className="subscription-plan-badge">Nåværende</span>}
                  </div>
                  <p className="subscription-plan-item-price">
                    {priceLabel}
                    {!plan.metadata?.displayPrice && <span>/mnd</span>}
                  </p>
                  <ul className="subscription-plan-item-features">
                    {featureList.length > 0 ? (
                      featureList.map((feature) => <li key={feature}>✓ {feature}</li>)
                    ) : (
                      <li className="disabled">Ingen planfunksjoner er definert i Stripe.</li>
                    )}
                  </ul>
                  <div className="subscription-plan-item-actions">
                    <button
                      type="button"
                      className={`subscription-btn ${isCurrent ? 'secondary' : 'primary'}`}
                      disabled={isCurrent || planLoadingCode === plan.code || contactOnly || freePlanLocked}
                      onClick={() => handlePlanChange(plan)}
                    >
                      {actionLabel}
                    </button>
                    {isCurrent && !isFreePlan(plan) && !contactOnly && (
                      <button
                        type="button"
                        className="subscription-btn secondary"
                        onClick={handlePlanCancellation}
                        disabled={planLoadingCode === plan.code}
                      >
                        {cancelAtPeriodEnd ? 'Angre kansellering' : 'Kanseller'}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <footer className="subscription-footer">
        <p>
          Har du spørsmål? <a href="mailto:hei@kichat.no">Kontakt oss</a>
        </p>
      </footer>
    </div>
  );
};

export default SubscriptionPage;
